/**
 * The app's view of the swarm.
 *
 * Two sources, because the API offers two and neither is sufficient alone: the socket carries the
 * Tick — temperature, regime, spikes — but **no per-fly detail**, and the Observation Engine needs
 * per-fly detail to draw anything. So the socket drives the clock and the REST read supplies the
 * bodies, refreshed on a slower cadence than the Tick arrives.
 *
 * The interesting decision is what happens when they disagree. The socket is authoritative for the
 * swarm's *state* and `flies` is authoritative for its *members*; a frame is only emitted when both
 * are present. Emitting a frame from the socket alone would draw the previous Tick's bodies under the
 * current Tick's temperature, which looks like data and is not.
 */

import type { SwarmFrame, SwarmFrameFly } from "../src/obs/layout.ts";
import { RealtimeClient, type ConnectionState, type SocketLike } from "../src/realtime.ts";
import type { TickFrame } from "@nouma/worker/protocol";
import type { CanaryPanel } from "@nouma/worker/canary-inputs";
import type {
  ChronicleRow,
  EconomySnapshot,
  FlyProfile,
  FlyRow,
  LineageSnapshot,
  Snapshot,
  TrustNetworkSnapshot,
} from "@nouma/worker/read-models";

export type {
  ChronicleRow,
  EconomySnapshot,
  FlyProfile,
  FlyRow,
  LineageSnapshot,
  Snapshot,
  TrustNetworkSnapshot,
};

/** What `GET /api/v1/chronicle` returns. */
export interface ChroniclePage {
  readonly events: ChronicleRow[];
  readonly nextCursor: number;
}

/** One Tick's retained record: the swarm's totals and the count behind each behaviour. */
export interface LabSeriesTick {
  readonly tick: number;
  readonly temperature: number;
  readonly regime: string;
  readonly totalSpikes: number;
  readonly mix: Readonly<Record<string, number>>;
}

/** What `GET /api/v1/lab/series` returns. */
export interface LabSeries {
  readonly ticks: LabSeriesTick[];
  readonly flies: ReadonlyArray<{
    readonly id: number;
    readonly house: string | null;
    readonly birthTick: number;
    readonly stage: string;
  }>;
}

/**
 * What `GET /api/v1/lab/spikes` returns.
 *
 * `flies[i].spikes[j]` is fly `flies[i].id`'s spike count in Tick `ticks[j]`. The two arrays are the
 * same length by construction — the route pads a short series at the front, where the Ticks it is
 * missing are — so a raster can index them together without a bounds check that would hide a bug.
 */
export interface LabSpikes {
  readonly ticks: number[];
  readonly flies: ReadonlyArray<{ readonly id: number; readonly spikes: number[] }>;
}

/**
 * What `GET /api/v1/lab/dose` returns.
 *
 * `delta` is `after - baseline`, computed on the Worker. It is returned rather than left to the
 * browser because it is the quantity the chart plots and the one a reader would check: doing the
 * subtraction twice invites the two to disagree, and the version on screen is the one nobody can
 * re-derive.
 */
export interface LabDose {
  readonly points: ReadonlyArray<{
    readonly tick: number;
    readonly intensity: number;
    readonly module: string;
    readonly after: number;
    readonly baseline: number;
    readonly delta: number;
  }>;
}

/**
 * What `GET /api/v1/flies/:id/drives` returns.
 *
 * Mirrors `Intent` as it survives JSON: `Amount` is a `bigint` in the economy and a decimal string on
 * the wire, because `JSON.stringify` throws on a bigint rather than rounding it.
 */
export interface FlyDriveResponse {
  readonly tick: number;
  readonly intent: {
    readonly kind: string;
    /** `null` for house-level or protocol-level counterparties. */
    readonly counterparty: number | null;
    readonly amount: string;
    /** The `D_i` breakdown the engine used to reach this decision. */
    readonly rationale: {
      readonly reward: number;
      readonly hunger: number;
      readonly social: number;
      readonly socialScore: number;
      readonly total: number;
      readonly weights: { readonly alpha: number; readonly beta: number; readonly gamma: number };
      readonly epigenetics: "hot-bonus" | "cold-bonus" | "neutral";
    };
  };
}

export interface SwarmApiOptions {
  /**
   * Origin of the Worker. `""` means same-origin, which is what a Pages deployment behind a route
   * rule wants; a development build points at the local `wrangler dev`.
   */
  readonly baseUrl: string;
  /** Injected so the app can be driven in a test without a network or a socket. */
  readonly socketFactory?: (url: string) => SocketLike;
  readonly fetchFn?: typeof fetch;
}

export interface SwarmHandlers {
  readonly onFrame: (frame: SwarmFrame) => void;
  readonly onSnapshot?: (snapshot: Snapshot) => void;
  /**
   * Every Tick, whether or not a frame could be built from it.
   *
   * `onFrame` fires only when both halves are present, which is right for the canvas and wrong for a
   * screen that wants the clock — the event feed and the telemetry panels need the Tick itself and
   * have no use for per-fly bodies.
   */
  readonly onTick?: (tick: TickFrame) => void;
  readonly onState?: (state: ConnectionState) => void;
  readonly onProblem?: (problem: { code: string; message: string }) => void;
}

/**
 * Turns a headline snapshot into a Tick frame, for the initial paint.
 *
 * The per-Tick settlement figures are not part of the headline read, so they are zeroed rather than
 * invented. The observation hall does not draw them — but a plausible `gross` would be worse than an
 * obviously empty one if anything ever did.
 */
function seedFromSnapshot(snapshot: Snapshot): TickFrame {
  return {
    tick: snapshot.tick,
    temperature: snapshot.temperature,
    regime: snapshot.marketState,
    totalSpikes: snapshot.totalSpikes,
    checksum: snapshot.checksum,
    population: snapshot.population,
    settlements: 0,
    gross: "0",
    folded: "0",
    foldRatio: null,
    at: Date.now(),
  };
}

/** The API's `FlyRow` carries more than the canvas needs; the canvas needs exactly this. */
function toFrameFly(row: FlyRow, spikeEstimate: number): SwarmFrameFly {
  return {
    id: row.id,
    genome: row.genome,
    behaviour: row.behaviour ?? "rest",
    fitness: row.fitness,
    // The API does not report spikes per fly — that is neural state, not durable state. The canvas
    // needs *a* magnitude, so it comes from what the row does carry. Documented rather than hidden:
    // this is a rendering input, not a measurement, and nothing economic depends on it.
    spikes: spikeEstimate,
  };
}

/**
 * Estimates the per-fly spike magnitude the canvas draws from.
 *
 * Fitness is already a 0..1 normalisation of recent activity, so scaling it against the swarm's
 * average keeps the picture's overall brightness tied to the Tick's real `totalSpikes` while letting
 * the distribution across flies follow what the API actually reports.
 */
function spikeEstimateFor(row: FlyRow, averageFitness: number, totalSpikes: number, flyCount: number): number {
  const base = flyCount === 0 ? 0 : totalSpikes / flyCount;
  if (averageFitness <= 0) return Math.max(0, base);
  return Math.max(0, (base * row.fitness) / averageFitness);
}

export class SwarmApi {
  private readonly options: SwarmApiOptions;
  private readonly fetchFn: typeof fetch;
  private readonly client: RealtimeClient;

  private flies: FlyRow[] = [];
  private latest: TickFrame | null = null;
  private handlers: SwarmHandlers | null = null;
  /** Frames emitted since the last fly refresh, so the two cadences can differ. */
  private sinceRefresh = 0;

  /** Re-reads the roster this often. Ticks are a minute apart; the roster changes slowly. */
  static readonly ROSTER_EVERY_TICKS = 5;

  constructor(options: SwarmApiOptions) {
    this.options = options;
    this.fetchFn = options.fetchFn ?? ((...args) => fetch(...args));

    this.client = new RealtimeClient(
      {
        url: `${options.baseUrl.replace(/^http/, "ws")}/api/v1/stream`,
        ...(options.socketFactory === undefined ? {} : { socketFactory: options.socketFactory }),
      },
      {
        onSnapshot: (snapshot) => this.handlers?.onSnapshot?.(snapshot),
        onTick: (tick) => {
          this.latest = tick;
          this.handlers?.onTick?.(tick);
          void this.emit();
        },
        onState: (state) => this.handlers?.onState?.(state),
        onProblem: (problem) => this.handlers?.onProblem?.(problem),
      },
    );
  }

  get state(): ConnectionState {
    return this.client.state;
  }

  async start(handlers: SwarmHandlers): Promise<void> {
    this.handlers = handlers;

    // Read the roster, then seed from the REST snapshot. Without the seed the picture would wait for
    // the first socket Tick — up to a minute of blank canvas on a page that had the data available
    // the whole time. The socket then keeps it current.
    await this.refreshRoster();

    try {
      const snapshot = await this.snapshot();
      handlers.onSnapshot?.(snapshot);
      this.latest = seedFromSnapshot(snapshot);
      await this.emit();
    } catch (error) {
      // Not fatal: the socket's opening snapshot arrives immediately and will seed it. Reported so a
      // REST-only failure is not invisible.
      handlers.onProblem?.({
        code: "seed_failed",
        message: error instanceof Error ? error.message : String(error),
      });
    }

    this.client.connect();
  }

  stop(): void {
    this.client.stop();
    this.handlers = null;
  }

  /** Reads the roster and, if a Tick has already arrived, emits a frame from the pair. */
  async refreshRoster(): Promise<FlyRow[]> {
    this.flies = await this.listFlies();
    this.sinceRefresh = 0;
    if (this.latest !== null) await this.emit();
    return this.flies;
  }

  /** Reads the swarm headline without waiting for a Tick. */
  async snapshot(): Promise<Snapshot> {
    return await this.get<Snapshot>("/api/v1/swarm/snapshot");
  }

  /**
   * Reads one page of the event stream.
   *
   * `cursor` is the id the caller last saw, not an offset: the feed grows at the head, so an offset
   * would skip or repeat events whenever a Tick landed between two reads.
   */
  async chronicle(cursor = 0, limit = 50): Promise<ChroniclePage> {
    return await this.get<ChroniclePage>(`/api/v1/chronicle?cursor=${cursor}&limit=${limit}`);
  }

  async fly(id: number): Promise<FlyRow> {
    return await this.get<FlyRow>(`/api/v1/flies/${id}`);
  }

  /**
   * Why one fly acted as it did in the most recent Tick.
   *
   * `null` rather than a throw on `503`. That status is the Worker saying "this instance has not
   * Ticked yet, so there is no reason to show" — a true statement about a swarm that just woke up,
   * not a failure. Turning it into an exception would make the drawer render an error where the
   * honest answer is "not yet", and the two need to look different.
   */
  async drive(id: number): Promise<FlyDriveResponse | null> {
    try {
      return await this.get<FlyDriveResponse>(`/api/v1/flies/${id}/drives`);
    } catch {
      return null;
    }
  }

  /**
   * The lab's retained history.
   *
   * One request for the whole screen rather than one per panel: the three charts annotate each other
   * with absolute tick numbers, and separate calls could land on different windows, which is a
   * disagreement a reader has no way to see.
   */
  async labSeries(limit = 120): Promise<LabSeries> {
    return await this.get<LabSeries>(`/api/v1/lab/series?limit=${limit}`);
  }

  /**
   * One sample per fly per Tick, for the raster.
   *
   * Its own request because its shape is different from the series': 48 samples a Tick against one
   * row a Tick per behaviour. A screen that did not want the raster should not pay for it.
   */
  async labSpikes(limit = 60): Promise<LabSpikes> {
    return await this.get<LabSpikes>(`/api/v1/lab/spikes?limit=${limit}`);
  }

  /**
   * What the swarm did after each visitor stimulus.
   *
   * The one lab series a visitor can add to by acting. Read on the same screen as the others so the
   * point a poke adds appears beside the charts that explain it.
   */
  async labDose(limit = 40): Promise<LabDose> {
    return await this.get<LabDose>(`/api/v1/lab/dose?limit=${limit}`);
  }

  /** Liveness and deployment identity. Read by the docs screen so it can say which instance you are on. */
  async health(): Promise<Record<string, unknown>> {
    return await this.get<Record<string, unknown>>("/api/v1/health");
  }

  /**
   * One contract's getters.
   *
   * A single method rather than one per contract: the Worker exposes `GET /chain/:name`, and a method
   * per module would be four more copies of the same two lines.
   */
  async contract(name: string): Promise<Record<string, unknown>> {
    return await this.get<Record<string, unknown>>(`/api/v1/chain/${name}`);
  }

  /**
   * The public telemetry panel.
   *
   * Served by the Worker rather than assembled here: the panel is a published artefact, and a front
   * end that computed its own metrics would be a second definition of them.
   */
  async canary(): Promise<CanaryPanel> {
    return await this.get<CanaryPanel>("/api/v1/canary/public");
  }

  /**
   * The swarm as an economy: what netting cancelled, how wealth is spread, and what every fly decided.
   *
   * One request rather than three because these are three views of one Tick. Fetched separately they
   * could arrive from different Ticks, and a panel showing a distribution from one moment beside flows
   * from another would have no way to say so.
   *
   * `settlement` comes back in the payload. This deployment answers `read-only`, and a panel is expected
   * to read that field rather than render a settled figure that means "not attempted".
   */
  async economy(limit = 120): Promise<EconomySnapshot> {
    return await this.get<EconomySnapshot>(`/api/v1/economy?limit=${limit}`);
  }

  /** The roster. Named after the endpoint rather than after the field it fills. */
  async listFlies(limit = 48): Promise<FlyRow[]> {
    const body = await this.get<{ flies: FlyRow[] }>(`/api/v1/flies?limit=${limit}`);
    return body.flies;
  }

  /** The global trust network and social metrics. */
  async trustNetwork(): Promise<TrustNetworkSnapshot> {
    return await this.get<TrustNetworkSnapshot>("/api/v1/trust");
  }

  /** The generational tree and turnover timeline. */
  async lineage(): Promise<LineageSnapshot> {
    return await this.get<LineageSnapshot>("/api/v1/lineage");
  }

  /** Full profile for an individual fly including trust partners and ledger summary. */
  async flyProfile(id: number): Promise<FlyProfile> {
    return await this.get<FlyProfile>(`/api/v1/flies/${id}/profile`);
  }

  /** A visitor stimulus. Recorded server-side as a chronicle event. */
  async poke(module: string, intensity: number): Promise<{ tick: number; accepted: boolean }> {
    return await this.post<{ tick: number; accepted: boolean }>("/api/v1/poke", { module, intensity });
  }

  /** The most recent frame, or null before both halves have arrived. */
  get frame(): SwarmFrame | null {
    if (this.latest === null || this.flies.length === 0) return null;
    return this.compose(this.latest, this.flies);
  }

  /** Builds a frame from a Tick and a roster. Pure, so the mapping is testable without either. */
  compose(tick: TickFrame, rows: readonly FlyRow[]): SwarmFrame {
    const count = rows.length;
    const averageFitness = count === 0 ? 0 : rows.reduce((sum, row) => sum + row.fitness, 0) / count;

    return {
      tick: tick.tick,
      temperature: tick.temperature,
      regime: tick.regime,
      flies: rows.map((row) =>
        toFrameFly(row, spikeEstimateFor(row, averageFitness, tick.totalSpikes, count)),
      ),
    };
  }

  private async emit(): Promise<void> {
    const frame = this.frame;
    if (frame === null) return;

    this.sinceRefresh += 1;
    this.handlers?.onFrame(frame);

    // The roster is refreshed on a slower cadence than the Tick arrives. Re-reading it every Tick
    // would spend a request per minute to learn that the swarm has the same 48 members.
    if (this.sinceRefresh >= SwarmApi.ROSTER_EVERY_TICKS) {
      try {
        await this.refreshRoster();
      } catch (error) {
        // A failed roster refresh must not stop the picture. The next Tick will try again.
        this.handlers?.onProblem?.({
          code: "roster_refresh_failed",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    }
  }

  private async get<T>(path: string): Promise<T> {
    const response = await this.fetchFn(`${this.options.baseUrl}${path}`, {
      headers: { accept: "application/json" },
    });
    if (!response.ok) throw new Error(`GET ${path} -> HTTP ${response.status}`);
    return (await response.json()) as T;
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const response = await this.fetchFn(`${this.options.baseUrl}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`POST ${path} -> HTTP ${response.status}`);
    return (await response.json()) as T;
  }
}
